/**
 * Bridge for opening the floating support chat from anywhere in the app.
 *
 * `useConnectChat` is not context-backed: it holds per-instance refs and mounts
 * the widget imperatively via `createRoot`. Calling the hook from a second
 * component would create a second instance with its own widget root and risk
 * two chat widgets in the same corner. Instead, the single mounted consumer
 * (FloatingChatButton, rendered once by DashboardLayout) stays authoritative:
 * other components *request* opening, and it *publishes* progress back.
 */

export type SupportChatIntent = 'agent' | 'report_issue';

/**
 * A request to open a *specific* case (ENG-4185).
 *
 * The launcher owns the panel, so "open case 1234" is a request like any other — it
 * just carries an id. It is separate from `SupportChatIntent` because an intent asks
 * for a conversation and a case id names an existing one.
 */
export interface SupportCaseReady {
  caseId: number;
}

export interface SupportChatState {
  /** A session is being provisioned (ChatJS load + start_chat round trip). */
  pending: boolean;
  error: string | null;
}

const OPEN_EVENT = 'pronto:open-support-chat';
const STATE_EVENT = 'pronto:support-chat-state';
const CASE_READY_EVENT = 'pronto:open-support-case';

/** Ask the floating chat widget to open, optionally with a specific intent. */
export const requestSupportChat = (intent?: SupportChatIntent): void => {
  document.dispatchEvent(
    new CustomEvent<{ intent?: SupportChatIntent }>(OPEN_EVENT, {
      detail: { intent },
    }),
  );
};

/**
 * Subscribe to open requests. Returns an unsubscribe function suitable for
 * returning directly from a useEffect.
 */
export const onSupportChatRequest = (
  handler: (intent?: SupportChatIntent) => void,
): (() => void) => {
  const listener = (e: Event): void => {
    const detail = (e as CustomEvent<{ intent?: SupportChatIntent }>).detail;
    handler(detail?.intent);
  };
  document.addEventListener(OPEN_EVENT, listener);
  return () => {
    document.removeEventListener(OPEN_EVENT, listener);
  };
};

/**
 * Ask the launcher to open one case.
 *
 * Called by the notification bridge when a scheduled run finishes: Thor writes the
 * notification, the notification socket delivers it, and this is how a socket frame
 * becomes a panel that is already showing the answer.
 */
export const notifySupportCaseReady = (caseId: number): void => {
  document.dispatchEvent(
    new CustomEvent<SupportCaseReady>(CASE_READY_EVENT, { detail: { caseId } }),
  );
};

/** Subscribe to case-ready requests. Returns an unsubscribe function. */
export const onSupportCaseReady = (
  handler: (caseId: number) => void,
): (() => void) => {
  const listener = (e: Event): void => {
    const detail = (e as CustomEvent<SupportCaseReady>).detail;
    if (detail?.caseId) {
      handler(detail.caseId);
    }
  };
  document.addEventListener(CASE_READY_EVENT, listener);
  return () => {
    document.removeEventListener(CASE_READY_EVENT, listener);
  };
};

/** Publish provisioning progress. Called only by the widget owner. */
export const publishSupportChatState = (state: SupportChatState): void => {
  document.dispatchEvent(
    new CustomEvent<SupportChatState>(STATE_EVENT, { detail: state }),
  );
};

/** Subscribe to provisioning progress. Returns an unsubscribe function. */
export const onSupportChatState = (
  handler: (state: SupportChatState) => void,
): (() => void) => {
  const listener = (e: Event): void => {
    handler((e as CustomEvent<SupportChatState>).detail);
  };
  document.addEventListener(STATE_EVENT, listener);
  return () => {
    document.removeEventListener(STATE_EVENT, listener);
  };
};
